import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Download, Trash2, Calendar } from 'lucide-react';
import { useLeads, useDeleteLead, useBulkDeleteLeads, type Lead } from '@/hooks/useLeads';
import { useAuthStore } from '@/stores/useAuthStore';
import { Card, FilterChip, LeadScoreBadge, RiskBadge, SkeletonCard, Button } from '@/components/ui';
import { exportToCSV } from '@/utils/exportCsv';
import { useT, useLanguage } from '@/i18n/LanguageContext';

type Filter = 'all' | 'today' | 'hot' | 'warm' | 'at-risk';

function isToday(dateStr: string) {
  const d = new Date(dateStr);
  const today = new Date();
  return d.toDateString() === today.toDateString();
}

function matchesFilter(lead: Lead, filter: Filter): boolean {
  switch (filter) {
    case 'today': return isToday(lead.createdAt);
    case 'hot': return lead.scoreBand === 'A';
    case 'warm': return lead.scoreBand === 'B';
    case 'at-risk': return lead.scoreBand === 'C' || lead.scoreBand === 'D';
    default: return true;
  }
}

function matchesSearch(lead: Lead, q: string): boolean {
  if (!q) return true;
  const lower = q.toLowerCase();
  return (
    lead.contact.fullName.toLowerCase().includes(lower) ||
    lead.entity.companyName.toLowerCase().includes(lower) ||
    lead.contact.email.toLowerCase().includes(lower)
  );
}

export default function LeadsPage() {
  const t = useT();
  const { lang } = useLanguage();
  const selectedEventId = useAuthStore((s) => s.selectedEventId);
  const { data: leads, isLoading } = useLeads(selectedEventId);
  const deleteLead = useDeleteLead();
  const bulkDelete = useBulkDeleteLeads();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const FILTERS: { key: Filter; label: string }[] = [
    { key: 'all', label: t.leads.filterAll },
    { key: 'today', label: t.leads.filterToday },
    { key: 'hot', label: t.leads.filterHot },
    { key: 'warm', label: t.leads.filterWarm },
    { key: 'at-risk', label: t.leads.filterAtRisk },
  ];

  // Clear selection when filter or search changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [filter, search]);

  const filtered = useMemo(() => {
    if (!leads) return [];
    return leads
      .filter((l) => matchesFilter(l, filter) && matchesSearch(l, search))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [leads, filter, search]);

  // No event selected guard
  if (!selectedEventId) {
    return (
      <div className="p-4 md:p-6 flex items-center justify-center min-h-[400px]">
        <Card className="p-8 text-center max-w-sm">
          <Calendar size={32} className="mx-auto text-reevol-text-muted mb-3" />
          <h2 className="text-lg font-bold text-reevol-text mb-2">{t.common.selectEvent}</h2>
          <p className="text-sm text-reevol-text-muted mb-4">
            {t.common.chooseEventLeads}
          </p>
          <Button onClick={() => navigate('/events')}>
            {t.common.goToEvents}
          </Button>
        </Card>
      </div>
    );
  }

  const handleDelete = (e: React.MouseEvent, leadId: string, companyName: string) => {
    e.stopPropagation();
    if (window.confirm(t.leads.confirmDelete.replace('{name}', companyName))) {
      deleteLead.mutate(leadId, {
        onSuccess: () => setSelectedIds((prev) => { const n = new Set(prev); n.delete(leadId); return n; }),
      });
    }
  };

  const toggleSelect = (e: React.MouseEvent | React.ChangeEvent, leadId: string) => {
    if ('stopPropagation' in e) e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((l) => l.id)));
    }
  };

  const handleBulkDelete = () => {
    const count = selectedIds.size;
    if (count === 0) return;
    if (window.confirm(t.leads.confirmBulkDelete.replace('{count}', String(count)))) {
      bulkDelete.mutate(Array.from(selectedIds), {
        onSuccess: () => setSelectedIds(new Set()),
      });
    }
  };

  const handleExport = () => {
    if (!filtered.length) return;
    const headers = ['Company', 'Contact', 'Title', 'Email', 'Country', 'Industry', 'Industry Relevance', 'Matched Industries', 'Score', 'Risk', 'Date'];
    const rows = filtered.map((l) => [
      l.entity.companyName,
      l.contact.fullName,
      l.contact.title,
      l.contact.email,
      l.entity.country,
      l.entity.industry || '',
      l.entity.industryRelevance || '',
      (l.entity.industryTags || []).join('; '),
      `${l.scoreBand} (${l.scoreNumeric})`,
      l.risk.riskTier,
      new Date(l.createdAt).toLocaleDateString(),
    ]);
    exportToCSV(headers, rows, `leads-${new Date().toISOString().slice(0, 10)}`);
  };

  const dateLocale = lang === 'zh' ? 'zh-CN' : 'en-US';

  const formatDate = (d: string) => {
    const date = new Date(d);
    if (isToday(d)) return t.common.today;
    return date.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' });
  };

  const allSelected = filtered.length > 0 && selectedIds.size === filtered.length;
  const someSelected = selectedIds.size > 0;

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-reevol-text">{t.leads.title}</h1>
        <button
          onClick={handleExport}
          className="flex items-center gap-1.5 text-sm text-reevol-text-secondary hover:text-reevol-blue transition-colors"
          title="Export CSV"
        >
          <Download size={16} />
          <span className="hidden sm:inline">{t.leads.export}</span>
        </button>
      </div>

      {/* Search + Filters */}
      <div className="space-y-3">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-reevol-text-muted" />
          <input
            type="text"
            placeholder={t.leads.searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 bg-reevol-input border border-reevol-border rounded-xl text-sm text-reevol-text placeholder:text-reevol-text-muted focus:outline-none focus:ring-2 focus:ring-reevol-blue/30"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {FILTERS.map((f) => (
            <FilterChip key={f.key} label={f.label} active={filter === f.key} onClick={() => setFilter(f.key)} />
          ))}
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
        </div>
      )}

      {/* Empty */}
      {!isLoading && filtered.length === 0 && (
        <Card className="p-8 text-center">
          <p className="text-reevol-text-muted text-sm">{t.leads.noLeadsFound}</p>
        </Card>
      )}

      {/* Desktop table */}
      {!isLoading && filtered.length > 0 && (
        <>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-reevol-border text-left">
                  <th className="pb-2 w-10 pr-2">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-reevol-border text-reevol-blue focus:ring-reevol-blue/30 cursor-pointer accent-[#3B82F6]"
                    />
                  </th>
                  <th className="pb-2 text-xs font-medium text-reevol-text-muted uppercase tracking-wider">{t.leads.company}</th>
                  <th className="pb-2 text-xs font-medium text-reevol-text-muted uppercase tracking-wider">{t.leads.contact}</th>
                  <th className="pb-2 text-xs font-medium text-reevol-text-muted uppercase tracking-wider">{t.leads.country}</th>
                  <th className="pb-2 text-xs font-medium text-reevol-text-muted uppercase tracking-wider">{t.leads.score}</th>
                  <th className="pb-2 text-xs font-medium text-reevol-text-muted uppercase tracking-wider">{t.leads.risk}</th>
                  <th className="pb-2 text-xs font-medium text-reevol-text-muted uppercase tracking-wider">{t.leads.date}</th>
                  <th className="pb-2 w-10" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((lead) => (
                  <tr
                    key={lead.id}
                    onClick={() => navigate(`/leads/${lead.id}`)}
                    className={`border-b border-reevol-border/50 hover:bg-gray-50 cursor-pointer transition-colors ${selectedIds.has(lead.id) ? 'bg-reevol-blue/5' : ''}`}
                  >
                    <td className="py-3 pr-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(lead.id)}
                        onChange={(e) => toggleSelect(e, lead.id)}
                        className="w-4 h-4 rounded border-reevol-border text-reevol-blue focus:ring-reevol-blue/30 cursor-pointer accent-[#3B82F6]"
                      />
                    </td>
                    <td className="py-3">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium text-reevol-text">{lead.entity.companyName}</p>
                        {lead.entity.industryRelevance === 'high' && (
                          <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" title={t.leads.highRelevance} />
                        )}
                        {lead.entity.industryRelevance === 'medium' && (
                          <span className="inline-block w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" title={t.leads.mediumRelevance} />
                        )}
                      </div>
                      <p className="text-xs text-reevol-text-muted">
                        {lead.entity.industry || ''}
                        {lead.entity.industryTags && lead.entity.industryTags.length > 0 && lead.entity.industry && ' · '}
                        {lead.entity.industryTags && lead.entity.industryTags.length > 0 && (
                          <span className="text-reevol-indigo">{lead.entity.industryTags.join(', ')}</span>
                        )}
                      </p>
                    </td>
                    <td className="py-3">
                      <p className="text-sm text-reevol-text">{lead.contact.fullName}</p>
                      <p className="text-xs text-reevol-text-muted">{lead.contact.title}</p>
                    </td>
                    <td className="py-3 text-sm text-reevol-text-secondary">{lead.entity.country || '—'}</td>
                    <td className="py-3"><LeadScoreBadge band={lead.scoreBand} /></td>
                    <td className="py-3"><RiskBadge tier={lead.risk.riskTier} /></td>
                    <td className="py-3 text-sm text-reevol-text-muted">{formatDate(lead.createdAt)}</td>
                    <td className="py-3">
                      <button
                        onClick={(e) => handleDelete(e, lead.id, lead.entity.companyName)}
                        className="p-1.5 rounded-lg text-reevol-text-muted hover:text-red-600 hover:bg-red-50 transition-colors"
                        title={t.leads.deleteLead}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="md:hidden space-y-2">
            {filtered.map((lead) => (
              <Card
                key={lead.id}
                className={`p-4 ${selectedIds.has(lead.id) ? 'ring-2 ring-reevol-blue/30' : ''}`}
                onClick={() => navigate(`/leads/${lead.id}`)}
              >
                <div className="flex items-start gap-3">
                  <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(lead.id)}
                      onChange={(e) => toggleSelect(e, lead.id)}
                      className="w-4 h-4 rounded border-reevol-border text-reevol-blue focus:ring-reevol-blue/30 cursor-pointer accent-[#3B82F6]"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-reevol-text truncate">{lead.entity.companyName}</p>
                    <p className="text-xs text-reevol-text-secondary mt-0.5">
                      {lead.contact.fullName} · {lead.contact.title}
                    </p>
                    <p className="text-xs text-reevol-text-muted mt-0.5">
                      {lead.entity.country || t.common.unknown} · {formatDate(lead.createdAt)}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <LeadScoreBadge band={lead.scoreBand} />
                    <RiskBadge tier={lead.risk.riskTier} />
                    <button
                      onClick={(e) => handleDelete(e, lead.id, lead.entity.companyName)}
                      className="p-1 rounded-lg text-reevol-text-muted hover:text-red-600 hover:bg-red-50 transition-colors"
                      title={t.leads.deleteLead}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      <p className="text-xs text-reevol-text-muted text-center">
        {filtered.length} {t.common.leads}
      </p>

      {/* Floating bulk action bar */}
      {someSelected && (
        <div className="fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4">
          <div className="flex items-center gap-3 px-5 py-3 bg-white border border-reevol-border rounded-full shadow-lg">
            <span className="text-sm font-medium text-reevol-text">
              {selectedIds.size} {t.leads.selected}
            </span>
            <div className="w-px h-5 bg-reevol-border" />
            <Button
              variant="danger"
              onClick={handleBulkDelete}
              loading={bulkDelete.isPending}
              icon={<Trash2 size={14} />}
            >
              {t.leads.deleteSelected}
            </Button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-sm text-reevol-text-muted hover:text-reevol-text transition-colors"
            >
              {t.common.cancel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
